import { useEffect, useState } from 'react';
import {
  Archive,
  CheckCircle2,
  XCircle,
  Folder,
  FolderOpen,
  RotateCcw,
  Plus,
  RefreshCw,
  CloudUpload,
  AlertTriangle,
} from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 36 — Tab "Respaldos" en Settings.
 *
 * Lista backups con metadata, permite crear/verificar/restaurar/abrir
 * carpeta, y configurar una carpeta sincronizada externa (Google Drive
 * Desktop / OneDrive) para que cada respaldo se duplique ahí.
 */

interface BackupItem {
  filename: string;
  filePath: string;
  size: number;
  createdAt: string;
  // Sprint 12 metadata sidecar (presente si el .json existe).
  sha256?: string;
  integrityOk?: boolean;
  appVersion?: string;
  schemaVersion?: string;
  tag?: string;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFechaLocal(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const ETIQUETA_TAG: Record<string, string> = {
  arranque: 'Al abrir',
  'cierre-dia': 'Cierre del día',
  periodico: 'Periódico',
  manual: 'Manual',
  'pre-restore': 'Pre-restauración',
};

export function BackupsForm() {
  const [items, setItems] = useState<BackupItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [accion, setAccion] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [carpetaSync, setCarpetaSync] = useState<string>('');
  const [carpetaSyncGuardada, setCarpetaSyncGuardada] = useState<string>('');

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.backup.listar();
    if (res.ok) setItems(res.data as BackupItem[]);
    setCargando(false);

    const cfg = await window.api.config.obtener('backup_carpeta_sync');
    if (cfg.ok && cfg.data) {
      setCarpetaSync(cfg.data);
      setCarpetaSyncGuardada(cfg.data);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const crearAhora = async () => {
    setAccion('crear');
    setMensaje(null);
    const res = await window.api.backup.crear();
    setAccion(null);
    if (res.ok) {
      setMensaje({ tipo: 'ok', texto: 'Respaldo creado correctamente.' });
      cargar();
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const abrirCarpetaBackups = async () => {
    await window.api.backup.abrirCarpeta();
  };

  const verificar = async (filename: string) => {
    setAccion(`verificar-${filename}`);
    setMensaje(null);
    const res = await window.api.backup.verificar(filename);
    setAccion(null);
    if (res.ok) {
      setMensaje({ tipo: 'ok', texto: `"${filename}" está íntegro y listo para restaurar.` });
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const restaurar = async (filename: string) => {
    const ok = await window.confirmar({
      titulo: '¿Restaurar este respaldo?',
      mensaje:
        `Esto reemplazará la base de datos actual con el respaldo "${filename}".\n\n` +
        `La base actual se preservará como copia de seguridad. La aplicación se reiniciará automáticamente al terminar.`,
      textoConfirmar: 'Sí, restaurar',
      variant: 'warn',
    });
    if (!ok) return;
    setAccion(`restaurar-${filename}`);
    setMensaje(null);
    const res = await window.api.backup.restaurar(filename);
    if (!res.ok) {
      setAccion(null);
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
    // Si OK, la app se va a relanzar — no hace falta limpiar estado.
  };

  const elegirCarpeta = async () => {
    const res = await window.api.backup.elegirCarpetaSync();
    if (res.ok && res.data) {
      setCarpetaSync(res.data);
    }
  };

  const guardarCarpetaSync = async () => {
    setAccion('guardar-sync');
    setMensaje(null);
    const valor = carpetaSync.trim();
    const res = await window.api.config.actualizar('backup_carpeta_sync', valor);
    setAccion(null);
    if (res.ok) {
      setCarpetaSyncGuardada(valor);
      setMensaje({
        tipo: 'ok',
        texto: valor
          ? 'Carpeta sincronizada guardada. Los próximos respaldos se copiarán ahí también.'
          : 'Carpeta sincronizada removida. Los respaldos sólo quedarán en la carpeta local.',
      });
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const cambioSync = carpetaSync.trim() !== carpetaSyncGuardada.trim();

  return (
    <div className="max-w-4xl space-y-5">
      <header className="flex items-start gap-3">
        <div className="rounded-full bg-slate-100 p-2 shrink-0">
          <Archive className="h-5 w-5 text-slate-700" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Respaldos</h2>
          <p className="text-sm text-slate-600 mt-1">
            Tus datos se respaldan automáticamente cada 3 horas, al abrir la app y al cerrar el día.
            Aquí puedes revisarlos, crear uno nuevo o restaurar uno previo.
          </p>
        </div>
      </header>

      {/* Acciones globales */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={crearAhora} disabled={accion === 'crear'} className="btn-primary">
          <Plus className="h-4 w-4" />
          {accion === 'crear' ? 'Creando…' : 'Crear respaldo ahora'}
        </button>
        <button onClick={abrirCarpetaBackups} className="btn-ghost">
          <FolderOpen className="h-4 w-4" />
          Abrir carpeta
        </button>
        <button onClick={cargar} className="btn-ghost" title="Refrescar lista">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Carpeta sincronizada externa */}
      <div className="card border-2 border-blue-200 bg-blue-50/40">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2 shrink-0">
            <CloudUpload className="h-5 w-5 text-blue-700" />
          </div>
          <div className="flex-1 space-y-2">
            <div>
              <h3 className="font-semibold text-slate-800">Carpeta sincronizada (Drive / OneDrive)</h3>
              <p className="text-sm text-slate-600">
                Si seleccionas una carpeta que ya está sincronizada con Google Drive
                o OneDrive, cada respaldo se subirá automáticamente a la nube. No
                necesitas configurar credenciales ni APIs — el cliente de Drive /
                OneDrive lo hace todo.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <input
                className="input flex-1 font-mono text-xs"
                value={carpetaSync}
                onChange={(e) => setCarpetaSync(e.target.value)}
                placeholder="C:\Users\Tu\Google Drive\RestaurantPOS-Backups"
              />
              <button onClick={elegirCarpeta} className="btn-ghost text-sm whitespace-nowrap">
                <Folder className="h-4 w-4" />
                Examinar…
              </button>
            </div>
            {cambioSync && (
              <button
                onClick={guardarCarpetaSync}
                disabled={accion === 'guardar-sync'}
                className="btn-primary text-sm"
              >
                Guardar carpeta
              </button>
            )}
            {!cambioSync && carpetaSyncGuardada && (
              <p className="text-xs text-emerald-700">
                ✓ Los próximos respaldos se copiarán también a esta carpeta.
              </p>
            )}
          </div>
        </div>
      </div>

      {mensaje && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            mensaje.tipo === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {mensaje.texto}
        </div>
      )}

      {/* Lista de backups */}
      {cargando ? (
        <div className="card text-center py-6 text-slate-400">Cargando respaldos…</div>
      ) : items.length === 0 ? (
        <div className="card text-center py-8 text-slate-500">
          <Archive className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Aún no hay respaldos.</p>
          <p className="text-sm">Toca "Crear respaldo ahora" para hacer el primero.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => {
            const integridad = b.integrityOk;
            const esCorrupto = b.filename.startsWith('corrupt-');
            return (
              <div
                key={b.filename}
                className={`card flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${
                  esCorrupto ? 'border-red-200 bg-red-50/30' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs truncate">{b.filename}</span>
                    {esCorrupto ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[10px] font-bold">
                        <XCircle className="h-3 w-3" /> Corrupto
                      </span>
                    ) : integridad === true ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-bold">
                        <CheckCircle2 className="h-3 w-3" /> Verificado
                      </span>
                    ) : integridad === false ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold">
                        <AlertTriangle className="h-3 w-3" /> Sin verificar
                      </span>
                    ) : null}
                    {b.tag && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px]">
                        {ETIQUETA_TAG[b.tag] ?? b.tag}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    <span>{formatFechaLocal(b.createdAt)}</span>
                    <span>{formatBytes(b.size)}</span>
                    {b.appVersion && <span>v{b.appVersion}</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap shrink-0">
                  <button
                    onClick={() => verificar(b.filename)}
                    disabled={!!accion || esCorrupto}
                    className="btn-ghost text-xs"
                  >
                    {accion === `verificar-${b.filename}` ? 'Verificando…' : 'Verificar'}
                  </button>
                  <button
                    onClick={() => restaurar(b.filename)}
                    disabled={!!accion || esCorrupto || integridad === false}
                    className="btn-danger text-xs"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {accion === `restaurar-${b.filename}` ? 'Restaurando…' : 'Restaurar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
